import { useState, useEffect } from 'react';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
    deleteClientOption
} from '../../../services/clientService';
import Toast from '../../ui/toasts/Toast';
import { SecurityAttributes, ClientOptionEnum } from '../../../utils/data/enums';
import { useAuthContext } from '../../../store/Contexts';
import { Dropdown } from '../../helpers/Dropdown';
import ClientOptionButtons from './ClientOptionButtons'
import { fetchEmploymentStatuses } from "../../../services/systemService";
import { fetchEmploymentCategories } from "../../../services/systemService";
import { deepCloneArray } from '../../../utils/utils';
import { EmployementCategoryEnum, EmployementStatusEnum } from '../../../utils/data/enums';
import { getText } from '../../../i18n';

const ClientSettingAccount = () => {

    const { hasAttribute } = useAuthContext();

    const createAccountAccessEndDateRule = () => {
        return { employmentCategoryId: 0, employmentStatusId: 0, daysUntilAccessEnds: 0 }
    }

    const [accountAccessEndDateRules, setAccountAccessEndDateRules] = useState([]);
    const [initialAccountAccessEndDateRules, setInitialAccountAccessEndDateRules] = useState([]);
    const [employmentStatuses, setEmploymentStatuses] = useState([]);
    const [employmentCategories, setEmploymentCategories] = useState([]);
    const [daysUntilAccessEnds, setDaysUntilAccessEnds] = useState([]);
    const [isNewClientOption, setIsNewClientOption] = useState(false);

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        await getLookupData();
        await getOptions();
    }

    const getLookupData = async () => {
        try {
            const categories = await fetchEmploymentCategories();

            const categoryOptions = categories.map((category) => ({
                value: category.id,
                label: category.name,
            }));

            // add the "select as the first item"
            categoryOptions.unshift({ value: 0, label: getText('msg_8598222918d3') })

            setEmploymentCategories(categoryOptions);

        } catch (error) {
            console.error("Could not fetch employment categories:", error);
            Toast.error(getText('msg_9b59fbd52185'));
        }

        try {
            const statuses = await fetchEmploymentStatuses();

            const statusOptions = statuses.map((status) => ({
                value: status.id,
                label: status.name,
            }));

            // add the "select as the first item"
            statusOptions.unshift({ value: 0, label: getText('msg_8598222918d3') })

            setEmploymentStatuses(statusOptions);

        } catch (error) {
            console.error("Could not fetch employment statuses", error);
            Toast.error(getText('msg_85e962141af6'));
        }

        let dayOptions = [];
        for (let i = 0; i <= 365; i++) {
            dayOptions.push({
                value: i,
                label: i.toString()
            });
        }

        dayOptions.unshift({ value: -1, label: getText('msg_8598222918d3') })

        setDaysUntilAccessEnds(dayOptions);
    }

    const getOptions = async () => {
        try {
            const clientOptions = await getClientOptions(0, [
                ClientOptionEnum.AccountAccessEndDateRules,
            ]);

            const clientOption = clientOptions[0];

            if (clientOption) {
                setIsNewClientOption(clientOption.usingDefaultValue);
            }

            let values = [];

            if (clientOption.value) {
                // replace the escaped quotes with quotes
                var jsonValue = clientOption.value.replace("\\\"", "\"");
                values = JSON.parse(jsonValue);
            }
            else
                values = [createAccountAccessEndDateRule()];

            setAccountAccessEndDateRules(values);
            setInitialAccountAccessEndDateRules(deepCloneArray(values));
        } catch (error) {
            Toast.error(getText('msg_eac0ad2dda48'));
            console.error('Could not load account settings', error);
        }
    };

    const handleEmploymentCategoryChange = (index, employmentCategoryId) => {
        setAccountAccessEndDateRules(prevRules => {
            const newRules = [...prevRules];
            newRules[index].employmentCategoryId = employmentCategoryId;
            return newRules;
        });
    }

    const handleEmploymentStatusChange = (index, employmentStatusId) => {
        setAccountAccessEndDateRules(prevRules => {
            const newRules = [...prevRules];
            newRules[index].employmentStatusId = employmentStatusId;
            return newRules;
        });
    }

    const handleDaysUntilAccessEndsChange = (index, daysUntilAccessEnds) => {
        setAccountAccessEndDateRules(prevRules => {
            const newRules = [...prevRules];
            newRules[index].daysUntilAccessEnds = daysUntilAccessEnds;
            return newRules;
        });
    }

    const validateRules = () => {

        let errorMessage = null;
        let index = 0;

        for (const rule of accountAccessEndDateRules) {

            const { employmentCategoryId, employmentStatusId, daysUntilAccessEnds } = rule;

            // if it is an empty row skip it
            if (!employmentCategoryId && !employmentStatusId && !daysUntilAccessEnds)
                continue;

            // rule missing category
            if (!employmentCategoryId)
                return "Category is required";

            // rule missing status
            if (!employmentStatusId)
                return "Status is required";

            // rule missing Days Until Access Ends    
            if (daysUntilAccessEnds === -1)
                return "Days until access ends is required";

            // if the category is employee you cant select an active status
            if (employmentCategoryId === EmployementCategoryEnum.Employee && employmentStatusId === EmployementStatusEnum.Active)
                return "A rule for the category of Employee and status of Active is not allowed";

            // if it isnt the last rule
            if (index != accountAccessEndDateRules.length) {
                const duplicateRecord = accountAccessEndDateRules.slice(index + 1).filter(rule =>
                    rule.employmentCategoryId === employmentCategoryId
                    && rule.employmentStatusId === employmentStatusId
                );

                if (duplicateRecord.length > 0) {
                    var category = employmentCategories.find(category => category.value === employmentCategoryId);
                    var status = employmentStatuses.find(status => status.value === employmentStatusId);

                    return `Duplicate rule found for category ${category.label} and status ${status.label}`;
                }
            }

            index++;
        }

        return errorMessage;
    }

    const handleSave = async () => {

        var errorMessage = validateRules();

        if (errorMessage) {
            Toast.error(errorMessage);
            return;
        }

        var cleanedRules = [];

        accountAccessEndDateRules.map(rule => {
            if (rule.employmentCategoryId)
                cleanedRules.push(rule);
        });

        if (cleanedRules.length === 0) {
            if (!isNewClientOption) {
                const deleteResponse = await deleteClientOption(0, ClientOptionEnum.AccountAccessEndDateRules);
                if (deleteResponse.error)
                    Toast.error(getText('msg_de35b52c7fa1'));
                else {
                    Toast.success(getText('msg_2e5ca8cab0d7'));
                    setIsNewClientOption(true);
                }
                return;
            }
        }

        const option = {
            clientOptionId: ClientOptionEnum.AccountAccessEndDateRules,
            value: JSON.stringify(cleanedRules)
        };

        let response;

        if (isNewClientOption) {
            response = await createClientOption(option, 0);
        } else {
            response = await updateClientOption(option, 0);
        }

        if (response.error)
            Toast.error(getText('msg_746efe9c4504'));
        else
            Toast.success(getText('msg_99cd9c5b5a7e'));

        setIsNewClientOption(false);
        setInitialAccountAccessEndDateRules(deepCloneArray(accountAccessEndDateRules));
    }

    const handleCancel = async () => {
        setAccountAccessEndDateRules(deepCloneArray(initialAccountAccessEndDateRules));
    }

    const handleDelete = async (indexToDelete) => {
        if (accountAccessEndDateRules.length === 1) setAccountAccessEndDateRules([createAccountAccessEndDateRule()]);
        else
            setAccountAccessEndDateRules(prevPairs =>
                prevPairs.filter((_, i) => i !== indexToDelete),
            );
    }

    const addNewRow = () => {
        const newRule = createAccountAccessEndDateRule();

        setAccountAccessEndDateRules(rules => [
            ...rules,
            newRule,
        ]);

    };

    const getEmploymentStatusesForCategory = (employmentCategoryId) => {

        const allStatuses = deepCloneArray(employmentStatuses);

        // if the category is employee you cant select an active status
        if (employmentCategoryId === EmployementCategoryEnum.Employee)
            return allStatuses.filter(status => status.value != EmployementStatusEnum.Active);
        else
            return allStatuses;
    }

    const getEmploymetCategoriesForStatus = (employmentStatusId) => {

        const allCategories = deepCloneArray(employmentCategories);

        // if the category is employee you cant select an active status
        if (employmentStatusId === EmployementStatusEnum.Active)
            return allCategories.filter(category => category.value != EmployementCategoryEnum.Employee);
        else
            return allCategories;
    }

    return (
        <>
            <table
                style={{
                    width: '100%',
                    marginBottom: '1rem',
                    marginTop: '36px',
                    verticalAlign: 'top',
                }}
                className="no-hover"
            >
                <thead>
                    <tr className="cursor-text">
                        <th style={{ width: '30%', paddingLeft: '20px' }}>{getText('msg_a3c686e711e4')}</th>
                        <th style={{ width: '30%', paddingLeft: '20px' }}>{getText('msg_bae7d5be7082')}</th>
                        <th style={{ width: '15%' }}>{getText('msg_09860141733e')}</th>
                        <th style={{ width: '25%' }}></th>
                    </tr>
                </thead>
                <tbody>
                    {accountAccessEndDateRules.map((rule, index) => {
                        return (
                            <tr>
                                <td>
                                    <Dropdown
                                        options={getEmploymetCategoriesForStatus(rule.employmentStatusId)}
                                        defaultValue={
                                            employmentCategories.find(item => item.value === rule.employmentCategoryId)
                                                ?.label}
                                        onChange={
                                            employmentCategoryId => {
                                                handleEmploymentCategoryChange(index, employmentCategoryId)
                                            }}
                                        disabled={employmentCategories.length === 0}
                                    />
                                </td>
                                <td>
                                    <Dropdown
                                        options={getEmploymentStatusesForCategory(rule.employmentCategoryId)}
                                        defaultValue={
                                            employmentStatuses.find(item => item.value === rule.employmentStatusId)
                                                ?.label}
                                        onChange={
                                            employmentStatusId => {
                                                handleEmploymentStatusChange(index, employmentStatusId)
                                            }}
                                        disabled={employmentStatuses.length === 0}
                                    />
                                </td>
                                <td>
                                    <Dropdown
                                        options={daysUntilAccessEnds}
                                        defaultValue={
                                            daysUntilAccessEnds.find(item => item.value === rule.daysUntilAccessEnds)
                                                ?.label}
                                        onChange={
                                            days => {
                                                handleDaysUntilAccessEndsChange(index, days)
                                            }}
                                        disabled={daysUntilAccessEnds.length === 0}
                                    />
                                </td>
                                <td>
                                    <i
                                        className="bi bi-trash me-2 cursor-pointer ms-3"
                                        style={{ color: 'red' }}
                                        onClick={() =>
                                            handleDelete(index)
                                        }
                                    />
                                </td>
                            </tr>)
                    })}
                </tbody>
            </table>

            <div
                style={{
                    color: 'var(--iNGAGED-Primary-Color)',
                    cursor: 'pointer',
                    width: 'fit-content',
                }}
                onClick={addNewRow}
            >
                {getText('msg_5e9eb8cca733')}
            </div>

            {hasAttribute(
                SecurityAttributes.ClientSettingsFullAccess
            ) && (<ClientOptionButtons handleSave={handleSave} handleCancel={handleCancel} />)}
        </>
    )
}

export default ClientSettingAccount;
